import * as React from 'react';
import { TabPanel, TabView, TabViewTabChangeEvent } from 'primereact/tabview';
import GroupsList from '../components/groups/GroupsList';
import GroupFilters from '../components/groups/GroupFilters';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import { fetchGroups, selectFilteredGroups, selectPaginatedAndFilteredGroups, setCurrentPage, setGlobalSearchString, setStatusFilter } from '../store/groupList';
import { RootState, useAppDispatch, useAppSelector } from '../store';
import { APIStatus, GroupStatus } from '../utils/types';
import { ProgressSpinner } from 'primereact/progressspinner';

import { ReactComponent as ProofingIcon } from '../assets/icons/proofing.svg';

export default function Dashboard() {
  const dispatch = useAppDispatch();
  const { role } = useAppSelector((state) => state.auth);

  React.useEffect(() => {
    dispatch(fetchGroups());
  }, [role]);

  React.useEffect(() => {
    dispatch(setGlobalSearchString(''));
  }, []);

  const { apiState, filters } = useAppSelector((state: RootState) => state.groupList);
  const filteredGroups = selectFilteredGroups(useAppSelector((state: RootState) => state.groupList));
  const paginatedGroups = selectPaginatedAndFilteredGroups(useAppSelector((state: RootState) => state.groupList));
  const { groups: allGroups } = useAppSelector((state: RootState) => state.groupList);

  const tabOptions = React.useMemo<unknown[]>(() => {
    return [
      {
        label: 'All requests',
        value: null,
        count: selectFilteredGroups({ groups: allGroups, filters: { ...filters, status: null } }).length,
      },
      {
        label: 'Tier 2',
        value: GroupStatus.TIER2_ALL,
        count: selectFilteredGroups({ groups: allGroups, filters: { ...filters, status: GroupStatus.TIER2_ALL } }).length,
      },
      {
        label: 'Rerate',
        value: GroupStatus.RERATE_ALL,
        count: selectFilteredGroups({ groups: allGroups, filters: { ...filters, status: GroupStatus.RERATE_ALL } }).length,
      },
      {
        label: 'High risk',
        value: GroupStatus.HIGH_RISK_ALL,
        count: selectFilteredGroups({ groups: allGroups, filters: { ...filters, status: GroupStatus.HIGH_RISK_ALL } }).length
      },
      {
        label: 'Complete',
        value: GroupStatus.COMPLETED,
        count: selectFilteredGroups({ groups: allGroups, filters: { ...filters, status: GroupStatus.COMPLETED } }).length,
      }
    ];
  }, [filteredGroups]);

  function tabChangeHandler(e: TabViewTabChangeEvent) {
    dispatch(setStatusFilter(tabOptions[e.index].value));
  }

  const currentTabIndex = React.useMemo(() => {
    const matchingTab = tabOptions.find((tab) => tab.value === filters.status);
    return tabOptions.indexOf(matchingTab);
  }, [filters.status]);

  return (
    <section className="flex flex-col flex-1 gap-4">
      {apiState === APIStatus.LOADING && (
        <div className="flex p-10 place-items-center">
          <ProgressSpinner className="w-[40px] h-[40px]" />
        </div>
      )}

      {apiState === APIStatus.ERROR && (
        <p className="font-bold text-warning-red">There was an error fetching groups, please reload to retry.</p>
      )}  

      {apiState === APIStatus.COMPLETE && (
        <>
          <GroupFilters />
        
          <section className="flex flex-col flex-1 min-h-full p-6 bg-white">
            <TabView
              activeIndex={currentTabIndex}
              onTabChange={tabChangeHandler}
              key={`tab-view_${currentTabIndex}`}
              className="flex flex-col flex-1"
              pt={
                {
                  panelContainer: {
                    className: !paginatedGroups.length && 'flex flex-col flex-1 justify-center'
                  }
                }
              }
            >
              {tabOptions.map((tab, tabIndex) => (
                <TabPanel
                  header={
                    <div className="flex items-center h-5">
                      <span>{tab.label}</span>
                      {!!tab.count && (
                        <span className="text-2xs text-allstate-blue flex items-center px-2 font-bold h-5 ml-2 border border-grey-4 rounded-[10px]">{tab.count}</span>
                      )}
                    </div>
                  }
                  key={tabIndex}
                >
                  <div className="flex flex-col self-start flex-1 pt-4"> 
                    {!!paginatedGroups.length && <GroupsList groups={paginatedGroups} />}
                    {!paginatedGroups.length && (
                      <div className="flex flex-col items-center justify-center flex-1 min-h-full gap-2 py-10">
                        <ProofingIcon className="text-allstate-blue w-[48px] h-[48px]" />
                        <div className="text-lg font-medium text-allstate-blue">No results found</div>
                        <p className="m-0">Sorry, there are no groups associated with the L number you selected.</p>
                        <p className="m-0">Try adjusting your search or filter selection.</p>
                      </div>
                    )}                    
                  </div>
                </TabPanel>
              ))}
            </TabView>
          </section>

          {filteredGroups.length > filters.perPage && (
            <section className="my-4">
              <Paginator
                rows={filters.perPage}
                totalRecords={filteredGroups.length}
                first={filters.perPage * (filters.currentPage - 1)}
                onPageChange={(e: PaginatorPageChangeEvent) => dispatch(setCurrentPage(e.page + 1))}
                template="PrevPageLink PageLinks NextPageLink"
              />
            </section>
          )}
        </>
      )}
    </section>
  );
}